import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchApiWithoutToken } from '../services/api';
import { Box, Typography, IconButton, Stack } from '@mui/material';
import { PrintContainer, ItineraryTable } from '../components/PrintStyles';
import { 
  PrintData, 
  CompanyInfo, 
  ApiTableResponse1,
  PrintDay,
  PrintLocation,
  PrintScheduleItem 
} from '../types/print';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const ItineraryPrintPage: React.FC = () => {
  const { itnNo } = useParams<{ itnNo: string }>();
  const [printData, setPrintData] = useState<PrintData | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [printFontSize, setPrintFontSize] = useState(11);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPrintData = async () => {
      try {
        const postData = [
          { "컬럼명": "@일정번호", "값": itnNo },
        ];

        const response = await fetchApiWithoutToken<ApiTableResponse1>("ITN_S_일정표_출력", postData);

        if (response.data?.table?.[0]?.일정JSON) {
          setPrintData(JSON.parse(response.data.table[0].일정JSON));
        } else {
          throw new Error('인쇄할 일정표를 찾을 수 없습니다.');
        }
      } catch (error) {
        console.error('Failed to fetch print data:', error);
        setError(error instanceof Error ? error.message : '인쇄용 일정표를 불러오는데 실패했습니다.');
      }
    };

    fetchPrintData();
  }, [itnNo]);

  const handlePrint = () => {
    window.print();
  };

  const handleFontSizeChange = (increase: boolean) => {
    setPrintFontSize(prev => increase ? prev + 1 : prev - 1);
  };

  const renderBasicInfo = () => {
    if (!printData) return null;
    
    const excludeKeys = ['itinerary', 'file_url', 'locations', 'places'];
    const rows: JSX.Element[] = [];
    let currentKey: string | null = null;
    let isSingleCellRow = false;

    Object.entries(printData)
      .filter(([key]) => !excludeKeys.includes(key))
      .forEach(([key, value]) => {
        if (typeof value === 'string') {
          const cells = value.split('||').map(cell => cell.trim());
          const firstCell = cells[0];
          const nonEmptyCells = cells.filter(cell => cell !== '');
          
          const isSingleCell = nonEmptyCells.length === 1;
          
          if (firstCell) {
            currentKey = firstCell;
            isSingleCellRow = isSingleCell;
            
            if (!isSingleCell) {
              let colSpan = cells.length - nonEmptyCells.length;
              rows.push(
                <tr key={key} style={{ fontSize: `${printFontSize}px` }}>
                  <td style={{ 
                    backgroundColor: '#f8f9fa',
                    padding: '2px 8px',
                    width: '120px',
                    borderTop: '1px solid #dee2e6'
                  }}>
                    {firstCell}
                  </td>
                  {nonEmptyCells.slice(1).map((cell, idx) => (
                    <td key={idx} style={{ 
                      padding: '2px 8px',
                      borderTop: '1px solid #dee2e6'
                    }}
                    colSpan={idx === nonEmptyCells.length - 2 ? colSpan + 1 : 1}>
                      {cell}
                    </td>
                  ))}
                </tr>
              );
            } else {
              rows.push(
                <tr key={key} style={{ fontSize: `${printFontSize}px` }}>
                  <td colSpan={7} style={{ 
                    padding: '2px 8px',
                    borderTop: isSingleCellRow ? 'none' : '1px solid #dee2e6'
                  }}>
                    {firstCell}
                  </td>
                </tr>
              );
            }
          } else if (currentKey && isSingleCellRow) {
            rows.push(
              <tr key={`${currentKey}-${rows.length}`} style={{ fontSize: `${printFontSize}px` }}>
                <td colSpan={7} style={{ 
                  padding: '2px 8px',
                  borderTop: 'none'
                }}>
                  {nonEmptyCells[0]}
                </td>
              </tr>
            );
          }
        }
      });

    return rows;
  };

  const renderDay = (day: PrintDay, dayIndex: number) => {
    const rows: React.ReactNode[] = [];
    let isFirstDayRow = true;
    let mealIndex = 0;

    day.locations.forEach((location: PrintLocation, locIndex: number) => {
      const isLastLocation = locIndex === day.locations.length - 1;

      location.schedule.forEach((item: PrintScheduleItem, scheduleIndex: number) => {
        const isLastSchedule = scheduleIndex === location.schedule.length - 1;
        const isLastInDay = isLastLocation && isLastSchedule;
        const isFirstInLocation = scheduleIndex === 0;

        const row = (
          <tr
            key={`day-${dayIndex}-loc-${locIndex}-sch-${scheduleIndex}`}
            className={isLastInDay ? 'day-separator' : ''}
          >
            <td style={{
              borderBottom: isLastInDay ? '1px solid #000' : 'none',
              visibility: isFirstDayRow ? 'visible' : 'hidden',
              textAlign: 'center'
            }}>
              {isFirstDayRow ? `${day.day}` : ''}
            </td>
            <td style={{
              borderBottom: isLastInDay ? '1px solid #000' : 'none',
              visibility: isFirstInLocation ? 'visible' : 'hidden',
              textAlign: 'left'
            }}>
              {isFirstInLocation ? location.place : ''}
            </td>
            <td style={{
              borderBottom: isLastInDay ? '1px solid #000' : 'none',
              textAlign: 'center'
            }}>
              {item.time || ''}
            </td>
            <td style={{
              borderBottom: isLastInDay ? '1px solid #000' : 'none',
              textAlign: 'center'
            }}>
              {item.flight || ''}
            </td>
            <td style={{ borderBottom: isLastInDay ? '1px solid #000' : 'none' }}>
              {item.details.join(', ')}
            </td>
            <td style={{
              borderBottom: isLastInDay ? '1px solid #000' : 'none',
              textAlign: 'left'
            }}>
              {mealIndex < day.meals.length ? day.meals[mealIndex] : ''}
            </td>
          </tr>
        );

        rows.push(row);
        isFirstDayRow = false;
        mealIndex++;
      });
    });

    return rows;
  };

  if (error) return <div>{error}</div>;
  if (!printData) return <div>Loading...</div>;

  return (
    <Box>
      {/* 컨트롤 버튼들 - 인쇄 시에는 숨김 */}
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center" 
        sx={{ 
          padding: '16px', 
          '@media print': { 
            display: 'none' 
          } 
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => handleFontSizeChange(false)}>
            <RemoveIcon />
          </IconButton>
          <Typography>{printFontSize}px</Typography>
          <IconButton onClick={() => handleFontSizeChange(true)}>
            <AddIcon />
          </IconButton>
        </Stack>
        <IconButton onClick={handlePrint}>
          <PrintIcon />
        </IconButton>
      </Stack>

      <PrintContainer>
        {/* 업체 정보는 숨김 처리 */}
        <Typography sx={{
          display: 'none'
        }}>
          [ {companyInfo?.업체명} , {companyInfo?.대표자} ] {companyInfo?.대표전화}, {companyInfo?.대표이메일}
        </Typography>

        <Typography sx={{
          fontSize: `${printFontSize + 2}px`,
          fontWeight: 'bold',
          marginBottom: '8px',
          textAlign: 'center'
        }}>
          {printData?.상품명}
        </Typography>

        {/* 기본 정보 테이블 */}
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          marginBottom: '16px',
          fontSize: `${printFontSize}px`
        }}>
          <tbody>
            {renderBasicInfo()}
          </tbody>
        </table>

        {/* 일정표 */}
        <Box sx={{ marginTop: '16px' }}>
          <ItineraryTable style={{ fontSize: `${printFontSize}px` }}>
            <thead style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000' }}>
              <tr>
                <th style={{ textAlign: 'center', fontWeight: 400, padding: '10px 2px' }}>일자</th>
                <th style={{ textAlign: 'left', fontWeight: 400, padding: '10px 2px' }}>지역</th>
                <th style={{ textAlign: 'center', fontWeight: 400, padding: '10px 2px' }}>시간</th>
                <th style={{ textAlign: 'center', fontWeight: 400, padding: '10px 2px' }}>교통편</th>
                <th style={{ fontWeight: 400, padding: '10px 2px' }}>주요일정</th>
                <th style={{ textAlign: 'left', fontWeight: 400, padding: '10px 2px' }}>식사</th>
              </tr>
            </thead>
            <tbody>
              {printData?.itinerary?.map((day, index) => renderDay(day, index))}
            </tbody>
          </ItineraryTable>
        </Box>
      </PrintContainer>
    </Box>
  );
};

export default ItineraryPrintPage; 